// Any custom axios configurations like headers or everything passed throughout the app

import axios from "axios";

// Create a new Axios instance with base URL
const instance = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL
});

// Get browser and device information
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  let browserName = "Unknown";
  let browserVersion = "Unknown";
  
  if (userAgent.indexOf("Chrome") > -1) {
    browserName = "Chrome";
    browserVersion = userAgent.match(/Chrome\/(\d+)/)?.[1] || "Unknown";
  } else if (userAgent.indexOf("Safari") > -1) {
    browserName = "Safari";
    browserVersion = userAgent.match(/Version\/(\d+)/)?.[1] || "Unknown";
  } else if (userAgent.indexOf("Firefox") > -1) {
    browserName = "Firefox";
    browserVersion = userAgent.match(/Firefox\/(\d+)/)?.[1] || "Unknown";
  } else if (userAgent.indexOf("Edge") > -1) {
    browserName = "Edge";
    browserVersion = userAgent.match(/Edge\/(\d+)/)?.[1] || "Unknown";
  }
  
  return { browserName, browserVersion };
};

const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  let deviceType = "Desktop";
  
  if (/Mobile|Android|iPhone|iPad|iPod/i.test(userAgent)) {
    deviceType = "Mobile";
  } else if (/Tablet|iPad/i.test(userAgent)) {
    deviceType = "Tablet";
  }
  
  return { deviceType, platform: navigator.platform };
};

// Add a request interceptor
instance.interceptors.request.use(
  (config) => {
    // Get the latest token for each request
    const token = localStorage.getItem("user") 
      ? JSON.parse(localStorage.getItem("user")).token 
      : null;
    
    // Modify the request config here, such as adding headers
    config.headers["Content-Type"] = "application/json";
    config.headers["Access-Control-Allow-Origin"] = "*";
    
    // Add authorization token if available
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add custom headers for audit logging (browser/device info)
    const browserInfo = getBrowserInfo();
    const deviceInfo = getDeviceInfo();
    
    config.headers["X-Client-Browser"] = browserInfo.browserName;
    config.headers["X-Client-Browser-Version"] = browserInfo.browserVersion;
    config.headers["X-Client-Device"] = deviceInfo.deviceType;
    config.headers["X-Client-Platform"] = deviceInfo.platform;
    
    // Add timestamp for tracking request duration
    config.metadata = { startTime: new Date() };
    
    // Optional: Log API call to console in development
    if (process.env.NODE_ENV === "development") {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        data: config.data,
        params: config.params,
      });
    }
    
    return config;
  },
  (error) => {
    console.error("[API Request Error]", error);
    return Promise.reject(error);
  }
);

// Add a response interceptor
instance.interceptors.response.use(
  (response) => {
    // Calculate request duration
    if (response.config.metadata?.startTime) {
      const duration = new Date() - response.config.metadata.startTime;
      response.duration = duration;
      
      // Optional: Log response in development
      if (process.env.NODE_ENV === "development") {
        console.log(
          `[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`,
          {
            status: response.status,
            duration: `${duration}ms`,
            data: response.data,
          }
        );
      }
    }
    
    // Modify the response data or headers if needed
    return response;
  },
  (error) => {
    // Calculate request duration for errors
    if (error.config?.metadata?.startTime) {
      const duration = new Date() - error.config.metadata.startTime;
      error.duration = duration;
    }
    
    // Log errors
    console.error("[API Error]", {
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      duration: error.duration ? `${error.duration}ms` : "N/A",
    });
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      // Unauthorized - possibly token expired
      console.warn("[Auth] Unauthorized access - token may be expired");
      
      // Optional: Redirect to login or refresh token
      // You can add custom logic here based on your auth flow
    }
    
    if (error.response?.status === 403) {
      // Forbidden - user doesn't have permissions
      console.warn("[Auth] Access forbidden - insufficient permissions");
    }
    
    return Promise.reject(error);
  }
);

export default instance;
